import React, { useState, useEffect } from 'react';
import SidePanel from '../SidePanel/SidePanel';
import {PAYMENT_SIDE_PANEL} from './../../config/SidePanelTypes';
import PaymentRow from './PaymentRow';
import { useIsPageDesktop } from '../../App';
import { formatDollarAmount, calculateDisplayPayments } from '../../util/EfupUtility';
import { AllPaymentTable, IndividualPaymentTable, ClaimantVendorPaymentTable, MultipleClaimantsPaymentTable, VendorPaymentTable } from '../../config/PaymentTableTypes';
import { toMMddYYYY } from '../../util/DateUtility';
import './PaymentTable.css';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

export default function PaymentTable(props) {
    const [sortColumn, setSortColumn] = useState("DATE");
    const [sortDirection, setSortDirection] = useState("desc");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [paymentUUID, setPaymentUUID] = useState("");
    const [paymentType, setPaymentType] = useState("");

    const isDesktop = useIsPageDesktop();

    //this sets the sort icon of the currently sorted column
    function iconClass(column) {
        const dir = sortColumn === column ? `_${sortDirection}ending` : '';

        return "cb-icon icon-sort" + dir;
    }

    function onRowSelect(uuid, type) {
        setPaymentUUID(uuid);
        setPaymentType(type);
        setIsModalOpen(true);
    }

    function setSort(column) {
        if (column) {
            if (column !== sortColumn) {
                setSortColumn(column);
                setSortDirection('asc');
                props.handleColumnSelect(column, 'asc');
            } else {
                const direction = sortDirection === 'asc' ? 'desc' : 'asc';
                setSortDirection(direction)
                props.handleColumnSelect(column, direction);
            }
        }
    }

    function getTableType() {
        switch (props.type) {
            case 'all':
                return AllPaymentTable;
            case 'individual':
                return IndividualPaymentTable;
            case 'claimantVendor':
                return ClaimantVendorPaymentTable;
            case 'multipleClaimants':
                return MultipleClaimantsPaymentTable;
            case 'vendor':
                return VendorPaymentTable;
            default:
                // Default to all payments table layout if unknown
                return AllPaymentTable;
        }
    }

    useEffect(() => {
        // Used during Cancel workflow to rollback selections
        const filters = props.tableFilters;
        if (filters) {
            setSortColumn(filters.sortColumn);
            setSortDirection(filters.sortDirection);
        }
    }, [props.tableFilters]);

    const tableTypeInfo = getTableType();

    const dataToDisplay = calculateDisplayPayments(props.pageSize, props.selectedPage, props.paymentData);

    return (
        <div className="cb-table-responsive payment-table">
            {isDesktop ?
                <table className="table table-sort table-arrows cb-table">
                    <thead className="table-light thead">
                        <tr>
                            {tableTypeInfo.map(item => {
                                if (!item.sort) {
                                    return <th className={`payment-table-header payment-table-${item.value}-header no-click`} key={`payment-table-${item.value}-header`}>
                                        <span data-testid={`payment-table-${item.value}-header`}>
                                            {item.display}{item.value === "vendor-id" && <>{" "}<div className="vendor-tool-tip">!
                                                <span data-testid="payment-total-tooltip" className="tooltiptext">
                                                    Vendor ID is the unique identifier for an enrolled vendor that is passed to Commerce by your system.
                                                    If a Vendor ID is N/A, it's an undefined vendor, and these vendors aren't enrolled and are given the
                                                    choice on how to receive their funds.
                                                </span>
                                            </div></>}
                                        </span>
                                    </th>
                                }
                                return <th className={`payment-table-header payment-table-${item.value}-header`} key={`payment-table-${item.value}-header`}
                                    tabIndex={0} onKeyDown={({key}) => key === "Enter" ? setSort(item.sort) : ""}>
                                    <span data-testid={`payment-table-${item.value}-header`} onClick={() => setSort(item.sort)}>
                                        {item.display}<i className={iconClass(item.sort)}></i>
                                    </span>
                                </th>
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {props.isLoading ?
                            <tr className='loading-spinner-row'>
                                <td colSpan={tableTypeInfo.length}>
                                    <LoadingSpinner blue data-testid='payment-table-loading-spinner' size={50} />
                                    <p className='loading-spinner-text'>Search in progress...</p>
                                    {dataToDisplay.length ? <p className='loading-spinner-text link' data-testid="payment-table-loading-cancel-link" onClick={() => props.cancel()}>Cancel search</p> : <></>}
                                </td>
                            </tr> :
                            <>{dataToDisplay.map(item => {
                                return <PaymentRow rowData={item} onRowSelect={onRowSelect} key={`payment-row-${item.uuid}`} typeInfo={tableTypeInfo} />
                            })}</>}
                    </tbody>
                </table> :
                <React.Fragment>
                    {props.type === "vendor" ?
                        dataToDisplay.map(item => {
                            return <div className="payment-table-mobile-wrapper cb-table" onClick={() => onRowSelect(item.uuid, item.paymentType)} key={`payment-row-${item.uuid}`}>
                                <div className="payment-table-mobile-data-top">
                                    <div className="payment-table-mobile-top-section-row">
                                        <div className="payment-table-mobile-left-side-row payment-mobile-vendor-name">{item.payeeNames.join(', ')}</div>
                                        <div className='payment-table-mobile-right-side-row'>{formatDollarAmount(item.amount)}</div>
                                    </div>
                                    <div className="payment-table-mobile-top-section-row">
                                        <div className='payment-table-mobile-left-side-row'>{item.vendorCode === 'N/A' ? 'Vendor ID is N/A' : item.vendorCode}</div>
                                        <div className='payment-table-mobile-right-side-row'>{item.statusCode}</div>
                                    </div>
                                </div>
                                <div className="payment-table-mobile-data-bottom">
                                    <div className="payment-mobile-payment-number">{item.paymentNumber}</div>
                                    <div className="payment-right-align payment-mobile-created-date">
                                        <div className="payment-mobile-created-date-label">Created:</div>
                                        <div>{toMMddYYYY(item.createdDate)}</div>
                                    </div>
                                </div>
                            </div>
                        })
                        :
                        dataToDisplay.map(item => {
                            return <div className="payment-table-mobile-wrapper cb-table" onClick={() => onRowSelect(item.uuid, item.paymentType)} key={`payment-row-${item.uuid}`}>
                                <div className="payment-table-mobile-data-top">
                                    <div className="payment-table-mobile-top-section-row">
                                        <div className="payment-table-mobile-left-side-row payment-mobile-party-name">{item.payeeNames.join(', ')}</div>
                                        <div className='payment-table-mobile-right-side-row'>{formatDollarAmount(item.amount)}</div>
                                    </div>
                                    <div className="payment-table-mobile-top-section-row">
                                        <div className='payment-table-mobile-left-side-row'></div>
                                        <div className='payment-table-mobile-right-side-row'>{item.statusCode}</div>
                                    </div>
                                </div>
                                <div className="payment-table-mobile-data-bottom">
                                    <div className="payment-mobile-payment-number">{item.paymentNumber}</div>
                                    <div className="payment-right-align payment-mobile-created-date">
                                        <div className="payment-mobile-created-date-label">Created:</div>
                                        <div>{toMMddYYYY(item.createdDate)}</div>
                                    </div>
                                </div>
                            </div>
                        })
                    }
                </React.Fragment>
            }
            <SidePanel
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                sidePanelType={PAYMENT_SIDE_PANEL}
                paymentUUID={paymentUUID}
                paymentType={paymentType} />
        </div>
    );
}